import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './foto.css';
import Header from '../components/menu/header';
import Footer from '../components/menu/footer';
import API from '../api/API';
import { BASE_URL } from '../api/ApiBase';

const Foto = () => {
  const { idAlbum, indexPhoto } = useParams();
  const navigate = useNavigate();

  const [view, setView] = useState('loading'); // 'loading', 'gallery', 'album', 'slideshow'
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [slideshowIndex, setSlideshowIndex] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragOver, setIsDragOver] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);
  const [uploadActive, setUploadActive] = useState(false);
  const [voteActive, setVoteActive] = useState(false);
  const [userVotes, setUserVotes] = useState(new Set());
  const slideshowImageRef = useRef(null);
  const logoOverlayRef = useRef(null);

  // Dati di esempio per gli album
  const [albums, setAlbums] = useState([])
  
  useEffect(() => {
    const loadAlbum = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await API.foto.disponibili();
        if(response.isSuccess){
          // Usa sempre endpoint API per le immagini
          const albumsWithImages = response.data.map(album => ({
            ...album,
            coverImage: album.copertina ? `${BASE_URL}/foto/image/${album.id}/${album.copertina}` : null,
            overlayUrl: album.overlay ? `${BASE_URL}/foto/image/${album.id}/${album.overlay}` : null,
            photo: album.photo?.map(foto => ({
              ...foto,
              url: `${BASE_URL}/foto/thumbnail/${album.id}/${foto}`,
              urlHigh: `${BASE_URL}/foto/image/${album.id}/${foto}`
            })) || []
          }));
          setAlbums(albumsWithImages);
          
          // Carica i voti dell'utente dai cookie
          loadUserVotesFromCookies();
        }
      } catch (err) {
        console.log('Errore caricamento foto:', err);
      } finally {
        setLoading(false);
      }
    };

    loadAlbum();
  }, []);

  // Gestione navigazione da URL
  useEffect(() => {
    if (albums.length === 0) return; // Aspetta che gli album siano caricati

    // Se c'è un idAlbum nell'URL
    if (idAlbum) {
      const album = albums.find(a => a.id === parseInt(idAlbum));
      if (album) {
        // Apri l'album
        setSelectedAlbum(album);

        // Carica stati upload/vote per questo album
        Promise.all([
          checkUploadPeriod(album.id),
          checkVotePeriod(album.id)
        ]);

        // Se c'è anche un indexPhoto nell'URL
        if (indexPhoto !== undefined) {
          const photoIndex = parseInt(indexPhoto);
          if (photoIndex >= 0 && photoIndex < (album.photo?.length || 0)) {
            setSlideshowIndex(photoIndex);
            setView('slideshow');
          } else {
            // Index non valido, torna alla vista album
            setView('album');
          }
        } else {
          // Solo album, nessuna foto specifica
          setView('album');
        }
      } else {
        // Album non trovato, torna alla galleria
        navigate('/foto', { replace: true });
        setView('gallery');
        setSelectedAlbum(null);
        setSlideshowIndex(-1);
      }
    } else {
      // Nessun idAlbum nell'URL, mostra la galleria
      setView('gallery');
      setSelectedAlbum(null);
      setSlideshowIndex(-1);
    }
  }, [albums, idAlbum, indexPhoto, navigate]);

  const loadUserVotesFromCookies = () => {
    const votes = new Set();
    document.cookie.split(';').forEach(cookie => {
      const [name, value] = cookie.trim().split('=');
      if (name.startsWith('vote_foto_')) {
        const fotoId = name.replace('vote_foto_', '');
        votes.add(parseInt(fotoId));
      }
    });
    setUserVotes(votes);
  };

  const checkUploadPeriod = async (albumId) => {
    API.foto.uploadIsActive(albumId).then(r => {
      try {
        if(r.isSuccess){
          setUploadActive(r.data);
        }
        else{
          setUploadActive(false)
        }
      } catch (err) {
        setUploadActive(false);
      }
    })
  };

  const checkVotePeriod = async (albumId) => {
    API.foto.voteIsActive(albumId).then(r => {
      try {
        if(r.isSuccess){
          setVoteActive(r.data);
        }
        else{
          setVoteActive(false)
        }
      } catch (err) {
        setVoteActive(false);
      }
    })
  };

  // Funzioni di navigazione
  const openAlbum = async (album) => {
    navigate(`/foto/${album.id}`);
    // Il view sarà impostato dal useEffect di navigazione URL
  };

  const closeAlbum = () => {
    navigate('/foto');
    // Il view sarà impostato dal useEffect di navigazione URL
  };

  const openSlideshow = (index) => {
    if (selectedAlbum) {
      navigate(`/foto/${selectedAlbum.id}/${index}`);
    }
    // Il view sarà impostato dal useEffect di navigazione URL
  };

  const closeSlideshow = () => {
    if (selectedAlbum) {
      navigate(`/foto/${selectedAlbum.id}`);
    }
    // Il view sarà impostato dal useEffect di navigazione URL
  };

  const nextSlide = () => {
    if (slideshowIndex < selectedAlbum.photo.length - 1) {
      const newIndex = slideshowIndex + 1;
      navigate(`/foto/${selectedAlbum.id}/${newIndex}`);
      // Il slideshowIndex sarà impostato dal useEffect di navigazione URL
    }
  };

  const prevSlide = () => {
    if (slideshowIndex > 0) {
      const newIndex = slideshowIndex - 1;
      navigate(`/foto/${selectedAlbum.id}/${newIndex}`);
      // Il slideshowIndex sarà impostato dal useEffect di navigazione URL
    }
  };

  // Funzioni di upload
  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    handleFileUpload(files);
  };

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    if (imageFiles.length > 0) {
      handleFileUpload(imageFiles);
    }
  }, []);

  const handleFileUpload = async (files) => {
    if (!selectedAlbum || files.length === 0 || !uploadActive) return;

    setLoading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      files.forEach(file => formData.append('files', file));

      const xhr = new XMLHttpRequest();
      
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          setUploadProgress((e.loaded / e.total) * 100);
        }
      });

      xhr.onload = function() {
        if (xhr.status === 200) {
          const newPhotos = JSON.parse(xhr.responseText);
          // Ricarica l'album per mostrare le nuove foto
          window.location.reload();
        } else {
          setError('Errore durante l\'upload');
        }
        setLoading(false);
        setUploadProgress(0);
      };

      xhr.onerror = function() {
        setError('Errore durante l\'upload');
        setLoading(false);
        setUploadProgress(0);
      };

      xhr.open('POST', await API.foto.upload(selectedAlbum.id));
      xhr.send(formData);

    } catch (err) {
      setError('Errore durante l\'upload');
      setLoading(false);
      setUploadProgress(0);
    }
  };

  const votePhoto = async (fotoId, rating) => {
    if (!voteActive || userVotes.has(fotoId)) return;

    try {
      const response = await fetch('/api/fotovote', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          foto: fotoId,
          voto: rating,
          owner: 'anonymous' // o un identificativo utente
        })
      });

      if (response.ok) {
        // Salva nel cookie che scade quando finisce il periodo di voto
        const voteEndDate = new Date(selectedAlbum.voteTo);
        document.cookie = `vote_foto_${fotoId}=1; expires=${voteEndDate.toUTCString()}; path=/`;
        
        // Aggiorna lo state locale
        setUserVotes(prev => new Set([...prev, fotoId]));
      }
    } catch (err) {
      console.error('Errore durante il voto:', err);
    }
  };


  const formatData = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const positionLogo = useCallback(() => {
    const img = slideshowImageRef.current;
    const logo = logoOverlayRef.current;

    if (img && logo) {
      const imgRect = img.getBoundingClientRect();
      const containerRect = img.parentElement.getBoundingClientRect();
      const logoImg = logo.querySelector('img');

      if (logoImg) {
        // Calcola le dimensioni del logo (8% della larghezza dell'immagine, coerente con download)
        const logoScale = 0.08;
        const logoWidth = imgRect.width * logoScale;
        const logoHeight = (logoImg.naturalHeight / logoImg.naturalWidth) * logoWidth;

        // Imposta le dimensioni del logo
        logoImg.style.width = `${logoWidth}px`;
        logoImg.style.height = `${logoHeight}px`;

        // Posiziona esattamente nell'angolo inferiore destro dell'immagine (senza margini)
        const left = imgRect.left - containerRect.left + imgRect.width - logoWidth;
        const top = imgRect.top - containerRect.top + imgRect.height - logoHeight;

        logo.style.left = `${left}px`;
        logo.style.top = `${top}px`;
        logo.style.right = 'auto';
        logo.style.bottom = 'auto';
      }
    }
  }, []);

  useEffect(() => {
    if (view === 'slideshow' && slideshowImageRef.current) {
      const img = slideshowImageRef.current;

      const handleLoad = () => {
        positionLogo();
      };

      const handleResize = () => {
        positionLogo();
      };

      const handleKeyDown = (e) => {
        if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
          e.preventDefault();
          prevSlide();
        } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
          e.preventDefault();
          nextSlide();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          closeSlideshow();
        }
      };

      img.addEventListener('load', handleLoad);
      window.addEventListener('resize', handleResize);
      window.addEventListener('keydown', handleKeyDown);

      // Posiziona subito se l'immagine è già caricata
      if (img.complete) {
        positionLogo();
      }

      return () => {
        img.removeEventListener('load', handleLoad);
        window.removeEventListener('resize', handleResize);
        window.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [view, slideshowIndex, positionLogo, prevSlide, nextSlide, closeSlideshow]);

  const shareOnWhatsApp = (foto = null) => {
    let message = `scoutballitalia.it - ${selectedAlbum.name}`;
    let url = window.location.href;

    if (foto) {
      // Condivisione di una singola foto
      message += ` - ${foto.titolo || 'Foto'}`;
      // Costruisci URL diretto alla foto nello slideshow
      const photoIndex = selectedAlbum.photo.findIndex(p => p === foto);
      if (photoIndex !== -1) {
        url = `${window.location.origin}/foto/${selectedAlbum.id}/${photoIndex}`;
      }
    } else {
      // Condivisione dell'album completo
      message += ` - ${selectedAlbum.photo?.length || 0} foto`;
      url = `${window.location.origin}/foto/${selectedAlbum.id}`;
    }

    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message + '\n\n' + url)}`;
    window.open(whatsappUrl, '_blank');
  };

  const downloadImageWithLogo = async (foto) => {
    try {
      // Crea un canvas per combinare l'immagine con il logo
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      // Carica l'immagine originale
      const img = new Image();
      img.crossOrigin = 'anonymous';

      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = foto.urlHigh || foto.url;
      });

      // Imposta le dimensioni del canvas
      canvas.width = img.width;
      canvas.height = img.height;

      // Disegna l'immagine di base
      ctx.drawImage(img, 0, 0);

      // Se c'è un overlay, caricalo e disegnalo
      if (selectedAlbum.overlayUrl) {
        const logo = new Image();
        logo.crossOrigin = 'anonymous';
        await new Promise((resolve, reject) => {
          logo.onload = resolve;
          logo.onerror = reject;
          logo.src = selectedAlbum.overlayUrl;
        });

        // Calcola le dimensioni e posizione del logo (esattamente nell'angolo inferiore destro, senza margini)
        // Mantiene le proporzioni originali dell'overlay (non deforma)
        const logoScale = 0.08; // 8% della larghezza dell'immagine (coerente con la visualizzazione)
        const logoWidth = img.width * logoScale;
        const logoHeight = (logo.height / logo.width) * logoWidth;
        const logoX = img.width - logoWidth; // Esattamente nel bordo destro
        const logoY = img.height - logoHeight; // Esattamente nel bordo inferiore

        // Aggiungi un'ombra al logo per migliorare la visibilità
        ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
        ctx.shadowBlur = 5;
        ctx.shadowOffsetX = 2;
        ctx.shadowOffsetY = 2;

        // Disegna il logo
        ctx.drawImage(logo, logoX, logoY, logoWidth, logoHeight);
      }

      // Scarica l'immagine combinata
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${foto.titolo || 'foto'}_${selectedAlbum.name}.jpg`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 'image/jpeg', 0.95);

    } catch (err) {
      console.error('Errore durante il download:', err);
      setError('Errore durante il download dell\'immagine');
    }
  };

  // Vista loading (caricamento iniziale)
  if (view === 'loading') {
    return (
      <div className="foto-page">
        <Header />
        <main className="foto-main">
          <div className="page-container">
            <div className="foto-loading">
              <div className="foto-loading-spinner"></div>
              <p>Caricamento...</p>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Vista galleria (lista album)
  if (view === 'gallery') {
    return (
      <div className="foto-page">
        <Header />
        
        <main className="foto-main">
          <div className="page-container">
            {/* Header */}
            <div className="foto-header">
              <div className="foto-title">
                <i className="fas fa-images"></i>
                <div>
                  <h1>Galleria Foto</h1>
                  <p>Rivivi i momenti più belli dei nostri tornei</p>
                </div>
              </div>
              
              <div className="foto-stats">
                <div className="foto-stat">
                  {albums.length} Album
                </div>
                <div className="foto-stat">
                  {albums.reduce((total, album) => total + (album.photo?.length || 0), 0)} Foto
                </div>
              </div>
            </div>

            {/* Grid album */}
            {loading ? (
              <div className="foto-loading">
                <div className="foto-loading-spinner"></div>
                <p>Caricamento album...</p>
              </div>
            ) : albums.length === 0 ? (
              <div className="foto-empty">
                <i className="fas fa-camera"></i>
                <h3>Nessun album disponibile</h3>
                <p>Non ci sono ancora album nella galleria.</p>
              </div>
            ) : (
              <div className="foto-grid">
                {albums.map((album, index) => (
                  <div
                    key={album.id}
                    className="foto-card"
                    onClick={() => openAlbum(album)}
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    {/* Immagine */}
                    <div className="foto-card-image">
                      {album.coverImage ? (
                        <img 
                          src={album.coverImage} 
                          alt={album.name}
                        />
                      ) : (
                        <div className="foto-card-placeholder">
                          <i className="fas fa-image"></i>
                          <span>Nessuna copertina</span>
                        </div>
                      )}
                      <div className="foto-image-overlay">
                        <div className="foto-badge">
                          {album.photo?.length || 0} foto
                        </div>
                      </div>
                    </div>

                    {/* Contenuto */}
                    <div className="foto-card-content">
                      <h3 className="foto-card-title">{album.name}</h3>
                      
                      <div className="foto-card-details">
                        <div className="foto-detail-item">
                          <i className="fas fa-calendar"></i>
                          <span>{formatData(album.date)}</span>
                        </div>
                        <div className="foto-detail-item">
                          <i className="fas fa-map-marker-alt"></i>
                          <span>{album.location}</span>
                        </div>
                      </div>

                      <div className="foto-card-action">
                        <span>Clicca per vedere l'album →</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </main>

        <Footer />
      </div>
    );
  }

  // Vista album
  if (view === 'album') {
    return (
      <div className="foto-page">
        <Header />
        
        <main className="foto-main">
          <div className="page-container">
            {/* Header album */}
            <div className="foto-album-header">
              <button
                onClick={closeAlbum}
                className="foto-btn foto-btn-back"
              >
                <i className="fas fa-chevron-left"></i>
                Torna alla galleria
              </button>
              
              <div className="foto-album-actions">
                {uploadActive ? 
                (<button 
                  className="foto-btn foto-btn-primary"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={loading}
                >
                  <i className="fas fa-plus"></i>
                  Aggiungi foto
                </button>) : (<></>)}
                <button
                  className="foto-btn foto-btn-secondary"
                  onClick={() => shareOnWhatsApp()}
                >
                  <i className="fab fa-whatsapp"></i>
                  Condividi su WhatsApp
                </button>
              </div>
            </div>

            {/* Input nascosto per file */}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            
            <div className="foto-album-info">
              <h1>{selectedAlbum.name}</h1>
              <p className="foto-album-edition">{selectedAlbum.edizione}</p>
              <div className="foto-album-meta">
                <span>
                  <i className="fas fa-calendar"></i>
                  {formatData(selectedAlbum.date)}
                </span>
                <span>
                  <i className="fas fa-map-marker-alt"></i>
                  {selectedAlbum.location}
                </span>
                <span>
                  <i className="fas fa-camera"></i>
                  {selectedAlbum.photo?.length || 0} foto
                </span>
              </div>
            </div>

            {/* Progress bar upload */}
            {loading && (
              <div className="foto-upload-progress">
                <div className="foto-progress-bar">
                  <div 
                    className="foto-progress-fill" 
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
                <span className="foto-progress-text">
                  Caricamento in corso... {Math.round(uploadProgress)}%
                </span>
              </div>
            )}

            {/* Area drag and drop */}
           {uploadActive && (!selectedAlbum.photo || selectedAlbum.photo.length === 0) && !loading && (
              <div 
                className={`foto-upload-area ${isDragOver ? 'drag-over' : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <div className="foto-upload-content">
                  <i className="fas fa-cloud-upload-alt"></i>
                  <h3>Trascina qui le foto oppure clicca per selezionarle</h3>
                  <p>Sono supportati tutti i formati immagine principali</p>
                  <button className="foto-btn foto-btn-primary">
                    <i className="fas fa-plus"></i>
                    Seleziona foto
                  </button>
                </div>
              </div>
            )}
            {/* {!uploadActive && (
              <div className="foto-upload-disabled">
                <i className="fas fa-clock"></i>
                <h3>Upload non disponibile</h3>
                <p>Il periodo per caricare nuove foto non è attualmente attivo</p>
              </div>
            )} */}

            {/* Grid foto */}
            {selectedAlbum.photo && selectedAlbum.photo.length > 0 && (
              <>
                <div className="foto-album-grid">
                  {selectedAlbum.photo.map((foto, index) => (
                    <div 
                      key={foto.id || index} 
                      className="foto-album-item"
                      onClick={() => openSlideshow(index)} // Sposta onClick sul container
                    >
                      <img
                        src={foto.url}
                        alt={foto.titolo || foto.nome}
                        style={{ pointerEvents: 'none' }} // Evita conflitti con overlay
                      />
                      
                      <div className="foto-album-overlay">
                        <div className="foto-album-icon">
                          <i className="fas fa-expand"></i>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </main>

        <Footer />
      </div>
    );
  }

  // Slideshow
  if (view === 'slideshow') {
    const currentFoto = selectedAlbum.photo[slideshowIndex];
    const hasVoted = userVotes.has(currentFoto.id);

    return (
      <div className="foto-slideshow">
        {/* Controlli slideshow */}
        <div className="foto-slideshow-header">
          <button
            onClick={closeSlideshow}
            className="foto-slideshow-control"
          >
            <i className="fas fa-times"></i>
          </button>
          
          <div className="foto-slideshow-counter">
            {slideshowIndex + 1} di {selectedAlbum.photo?.length || 0}
          </div>
          
          <div className="foto-slideshow-actions">
            {voteActive && !hasVoted && (
              <div className="foto-slideshow-vote">
                {[1,2,3,4,5].map(rating => (
                  <button
                    key={rating}
                    className="foto-slideshow-vote-btn"
                    onClick={() => votePhoto(currentFoto.id, rating)}
                  >
                    <i className="fas fa-star"></i>
                    {rating}
                  </button>
                ))}
              </div>
            )}
            
            {hasVoted && (
              <div className="foto-slideshow-voted">
                <i className="fas fa-check-circle"></i>
                Foto votata
              </div>
            )}
            <button
              className="foto-slideshow-control"
              onClick={() => downloadImageWithLogo(currentFoto)}
              title="Scarica foto con logo"
            >
              <i className="fas fa-download"></i>
            </button>
            <button
              className="foto-slideshow-control"
              onClick={() => shareOnWhatsApp(currentFoto)}
              title="Condividi su WhatsApp"
            >
              <i className="fab fa-whatsapp"></i>
            </button>
          </div>
        </div>

        {/* Navigazione */}
        {slideshowIndex > 0 && (
          <button
            onClick={prevSlide}
            className="foto-slideshow-nav foto-slideshow-prev"
          >
            <i className="fas fa-chevron-left"></i>
          </button>
        )}

        {slideshowIndex < (selectedAlbum.photo?.length || 0) - 1 && (
          <button
            onClick={nextSlide}
            className="foto-slideshow-nav foto-slideshow-next"
          >
            <i className="fas fa-chevron-right"></i>
          </button>
        )}

        {/* Immagine principale */}
        <div className="foto-slideshow-content">
          <div className="foto-slideshow-image">
            <img
              ref={slideshowImageRef}
              src={currentFoto.urlHigh || currentFoto.url}
              alt={currentFoto.titolo}
            />
            {/* Logo overlay */}
            {selectedAlbum.overlayUrl && (
              <div ref={logoOverlayRef} className="foto-logo-overlay">
                <img src={selectedAlbum.overlayUrl} alt="Logo" style={{ objectFit: 'contain' }} />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Fallback - non dovrebbe mai arrivare qui
  return (
    <div className="foto-page">
      <Header />
      <main className="foto-main">
        <div className="page-container">
          <div className="foto-loading">
            <div className="foto-loading-spinner"></div>
            <p>Caricamento...</p>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Foto;